import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLanguage } from './contexts/LanguageContext';

const ShareResultPage = () => {
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use global language context
  const { language } = useLanguage();

  // Simple inline translations
  const getText = (key) => {
    const texts = {
      es: {
        title: "¡Resultado Compartido!",
        subtitle: "Descubre el valor de esta marca calculado con YourBrandValue",
        loading: "Cargando resultado...",
        notFoundTitle: "Resultado no encontrado",
        notFoundMessage: "El resultado compartido que buscas no existe o ha expirado.",
        notFoundCTA: "🧮 Calcula el valor de tu marca",
        company: "Empresa",
        personal: "Marca Personal",
        calculatedWith: "Calculado con YourBrandValue",
        calculate: "🧮 Calcula el valor de tu marca",
        consultation: "📞 Consultoría personalizada",
        navCalculate: "🧮 Calcular mi marca",
        benefitsFastTitle: "Gratis y Rápido",
        benefitsFastDesc: "Calcula el valor de tu marca en menos de 5 minutos",
        benefitsAITitle: "IA Avanzada",
        benefitsAIDesc: "Análisis de sentimiento y benchmarking con IA",
        benefitsReportsTitle: "Reportes Detallados",
        benefitsReportsDesc: "PDFs ejecutivos con insights y recomendaciones",
        footerTagline: "Plataforma profesional de inteligencia de marca con IA avanzada",
        aboutMe: "Sobre Mí",
        advisory: "Asesoría",
        caseStudies: "Casos de Estudio"
      },
      en: {
        title: "Shared Result!",
        subtitle: "Discover this brand's value calculated with YourBrandValue",
        loading: "Loading result...",
        notFoundTitle: "Result not found",
        notFoundMessage: "The shared result you're looking for doesn't exist or has expired.",
        notFoundCTA: "🧮 Calculate your brand value",
        company: "Company",
        personal: "Personal Brand",
        calculatedWith: "Calculated with YourBrandValue",
        calculate: "🧮 Calculate your brand value",
        consultation: "📞 Personalised consultation",
        navCalculate: "🧮 Calculate my brand",
        benefitsFastTitle: "Free & Fast",
        benefitsFastDesc: "Calculate your brand value in less than 5 minutes",
        benefitsAITitle: "Advanced AI",
        benefitsAIDesc: "Sentiment analysis and benchmarking with AI",
        benefitsReportsTitle: "Detailed Reports",
        benefitsReportsDesc: "Executive PDFs with insights and recommendations",
        footerTagline: "Professional brand intelligence platform with advanced AI",
        aboutMe: "About Me",
        advisory: "Advisory",
        caseStudies: "Case Studies"
      }
    };
    
    return texts[language]?.[key] || texts.es[key] || key;
  };

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${resultId}`);
        
        if (!response.ok) {
          throw new Error(getText('notFoundTitle'));
        }
        
        const data = await response.json();
        setResult(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p>{getText('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center max-w-md mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">🔍</h1>
          <h2 className="text-2xl font-bold mb-4">{getText('notFoundTitle')}</h2>
          <p className="text-gray-300 mb-8">{getText('notFoundMessage')}</p>
          <Link
            to="/calculator"
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
          >
            {getText('notFoundCTA')}
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${window.location.origin}/r/${resultId}`;
  const ogImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/og-image/${resultId}`;
  
  // Dynamic meta content based on actual result data and language
  const formatValue = (value) => `€${value?.toLocaleString()}`;
  
  const pageTitle = result ? 
    (language === 'en' ? `My brand is worth ${formatValue(result.brand_value)} — YourBrandValue` : `Mi marca vale ${formatValue(result.brand_value)} — YourBrandValue`) : 
    (language === 'en' ? 'Brand Result — YourBrandValue' : 'Resultado de Marca — YourBrandValue');
    
  const pageDescription = result ? 
    (language === 'en' ? 
      `${result.brand_name} in ${result.industry} has a value of ${formatValue(result.brand_value)}. Calculate your brand value for free and compare it with your sector.` :
      `${result.brand_name} en ${result.industry} tiene un valor de ${formatValue(result.brand_value)}. Calcula gratis el valor de tu marca y compárala con tu sector.`
    ) :
    (language === 'en' ? 
      'Calculate your brand value for free and compare it with your sector.' :
      'Calcula gratis el valor de tu marca y compárala con tu sector.'
    );
    
  const twitterTitle = result ? 
    (language === 'en' ? `${result.brand_name} is worth ${formatValue(result.brand_value)} — YourBrandValue` : `${result.brand_name} vale ${formatValue(result.brand_value)} — YourBrandValue`) :
    pageTitle;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Meta Tags - ESPECÍFICOS AL RESULTADO */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="YourBrandValue" />
        <meta property="og:locale" content={language === 'es' ? 'es_ES' : 'en_US'} />
        
        {/* Twitter Card Meta Tags - ESPECÍFICOS AL RESULTADO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:site" content="@yourbrandvalue" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="text-xl font-bold text-white">
                Your<span className="text-purple-400">Brand</span>Value
              </Link>
              <Link
                to="/calculator"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
              >
                {getText('navCalculate')}
              </Link>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-white mb-4">
                {getText('title')}
              </h1>
              <p className="text-xl text-gray-300">
                {getText('subtitle')}
              </p>
            </div>

            {/* Result Card */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-3xl p-8 border border-white/10 text-center mb-12">
              <div className="text-6xl mb-6">💎</div>
              
              <h2 className="text-3xl font-bold text-white mb-2">
                {result.brand_name}
              </h2>
              
              <p className="text-lg text-gray-300 mb-6">
                {result.industry} • {result.brand_type === 'company' ? getText('company') : getText('personal')}
              </p>
              
              <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-4">
                €{result.brand_value?.toLocaleString()}
              </div>
              
              <p className="text-gray-400 mb-8">
                {getText('calculatedWith')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/calculator"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
                >
                  {getText('calculate')}
                </Link>
                <Link
                  to="/asesoria"
                  className="border-2 border-purple-500 text-purple-400 px-8 py-4 rounded-xl font-bold hover:bg-purple-500 hover:text-white transition-all duration-300"
                >
                  {getText('consultation')}
                </Link>
              </div>
            </div>

            {/* Benefits Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-800/50 p-6 rounded-xl border border-white/10 text-center">
                <div className="text-3xl mb-3">⚡</div>
                <h3 className="text-lg font-semibold text-white mb-2">{getText('benefitsFastTitle')}</h3>
                <p className="text-gray-300 text-sm">{getText('benefitsFastDesc')}</p>
              </div>
              <div className="bg-slate-800/50 p-6 rounded-xl border border-white/10 text-center">
                <div className="text-3xl mb-3">🤖</div>
                <h3 className="text-lg font-semibold text-white mb-2">{getText('benefitsAITitle')}</h3>
                <p className="text-gray-300 text-sm">{getText('benefitsAIDesc')}</p>
              </div>
              <div className="bg-slate-800/50 p-6 rounded-xl border border-white/10 text-center">
                <div className="text-3xl mb-3">📊</div>
                <h3 className="text-lg font-semibold text-white mb-2">{getText('benefitsReportsTitle')}</h3>
                <p className="text-gray-300 text-sm">{getText('benefitsReportsDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900/80 border-t border-white/10 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="text-2xl font-bold text-white mb-4">
              Your<span className="text-purple-400">Brand</span>Value
            </div>
            <p className="text-gray-400 mb-4">
              {getText('footerTagline')}
            </p>
            <div className="flex justify-center space-x-6 text-gray-400 text-sm">
              <Link to="/sobre-mi" className="hover:text-white transition-colors">{getText('aboutMe')}</Link>
              <Link to="/asesoria" className="hover:text-white transition-colors">{getText('advisory')}</Link>
              <Link to="/casos-estudio" className="hover:text-white transition-colors">{getText('caseStudies')}</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ShareResultPage;